import React from "react";
import "../components/pages/Activity.css";
import { WeeklyHoursChart } from "../widgets/WeeklyHoursChart";
import { useAttendanceWeeks } from "../widgets/useAttendanceWeeks";
import { AttendanceTable } from "../widgets/AttendanceTable";
import { OperatorsSummary } from "../widgets/OperatorsSummary";
import { SalarySummary } from "../widgets/SalarySummary";
import { API_ENDPOINTS, getAuthHeaders } from "../config/api";
import { getMonthRange, createTimestamp, calculateWorkMinutes } from "../utils/dateTime";
import { usePrefetchAdjacentMonths } from "../hooks/usePrefetch";
import { updateAttendanceInCache, addAttendanceToCache } from "../utils/cacheUpdate";
import { useBackgroundActivitySync } from "../hooks/useBackgroundSync";


/**
 * Page Activité - Affiche un histogramme groupé des heures travaillées par semaine et par opérateur
 * @param {{onNavigate: Function}} props
 */
export default function ActivityPage({ onNavigate }) {
  // État: Mois sélectionné (par défaut le mois actuel)
  const [selectedMonth, setSelectedMonth] = React.useState(new Date());
  
  // État: période calculée à partir du mois sélectionné
  const [range, setRange] = React.useState(() => getMonthRange(new Date()));
  
  // État: opérateurs sélectionnés pour le filtrage local (vide = tous sélectionnés)
  const [selectedOperatorIds, setSelectedOperatorIds] = React.useState([]);
  
  // État: Toast de confirmation
  const [toast, setToast] = React.useState(null);
  
  // Hook de données
  const { weeks, operators, series, maxY, rawData, loading, error, isRefreshing, refresh } = useAttendanceWeeks(
    range,
    selectedOperatorIds
  );
  
  // 🔮 Prefetch automatique des mois adjacents (N-1 et N+1)
  usePrefetchAdjacentMonths(selectedMonth);
  
  // 🔄 Sync en background toutes les 2 minutes
  useBackgroundActivitySync();

  // Afficher un toast temporaire
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Navigation entre mois
  const handleMonthChange = (direction) => {
    const newMonth = new Date(selectedMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else if (direction === 'next') {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setSelectedMonth(newMonth);
    setRange(getMonthRange(newMonth));
  };

  // Formatage du mois pour affichage
  const formatMonthDisplay = (month) => {
    return month.toLocaleDateString('fr-FR', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Handler pour mettre à jour une ligne du tableau
  const handleUpdateAttendance = async (rowId, field, value) => {
    console.log('📝 Update:', { rowId, field, value });
    
    const row = rawData.find(r => r.id === rowId);
    if (!row) return null;
    
    if (field === 'delete') {
      showToast('Suppression non implémentée', 'info');
      return null;
    }
    
    try {
      const updates = {};
      
      if (field === 'check_in' || field === 'check_out') {
        // ✅ Utiliser createTimestamp qui gère le timezone Europe/Paris
        const dateStr = row.local_date.split('T')[0]; // YYYY-MM-DD
        const timestamp = createTimestamp(dateStr, value);
        
        console.log('🔧 [Activity] Timestamp construit avec timezone:', {
          field,
          value_input: value,
          dateStr,
          timestamp
        });
        
        updates[field] = timestamp;
        
        // Recalcul work_minutes
        const checkIn = field === 'check_in' ? updates[field] : row.check_in;
        const checkOut = field === 'check_out' ? updates[field] : row.check_out;
        if (checkIn && checkOut) {
          const breakMins = row.break_minutes || 0;
          updates.work_minutes = calculateWorkMinutes(checkIn, checkOut, breakMins);
        }
      } else if (field === 'break_minutes') {
        const [h, m] = value.split(':').map(Number);
        updates.break_minutes = h * 60 + m;
        if (row.check_in && row.check_out) {
          updates.work_minutes = calculateWorkMinutes(row.check_in, row.check_out, updates.break_minutes);
        }
      }
      
      const url = API_ENDPOINTS.ATTENDANCE_DAY_UPDATE(rowId);
      console.log('📡 [Activity] Envoi PATCH:', url, updates);
      
      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      console.log('📡 [Activity] Réponse PATCH:', res.status, res.ok);
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ [Activity] Erreur PATCH:', res.status, errorText);
        throw new Error(errorText || 'Erreur mise à jour');
      }
      
      const result = await res.json();
      console.log('✅ [Activity] PATCH OK, backend répond:', result);
      
      // ✅ Vérifier que le backend a bien retourné success
      if (!result.success && !result.day) {
        throw new Error('Backend n\'a pas confirmé la modification');
      }
      
      const updatedRow = { ...row, ...updates };
      
      // ✅ MAJ immédiate dans cache
      updateAttendanceInCache(rowId, updates);
      
      // ✅ Toast SEULEMENT si succès confirmé par backend
      showToast('✅ Modification enregistrée en base de données', 'success');
      return updatedRow;
      
    } catch (err) {
      console.error('❌ Erreur update:', err);
      showToast(err.message || 'Erreur lors de la sauvegarde', 'error');
      return null;
    }
  };

  // Handler pour ajouter une nouvelle présence
  const handleAddAttendance = async (formData) => {
    try {
      const userId = localStorage.getItem('userId') || localStorage.getItem('uid') || localStorage.getItem('user_id');
      
      // Construire les timestamps (DST-safe avec Luxon)
      const date = formData.date;
      const checkInTime = createTimestamp(date, formData.check_in);
      const checkOutTime = createTimestamp(date, formData.check_out);
      
      // Convertir durée pause HH:MM en minutes
      const [breakHours, breakMins] = formData.break_duration.split(':').map(Number);
      const breakMinutes = breakHours * 60 + breakMins;
      
      // Calculer work_minutes (DST-safe avec Luxon)
      const workMinutes = calculateWorkMinutes(checkInTime, checkOutTime, breakMinutes);
      
      // Créer la présence
      const payload = {
        operator_id: Number(formData.operator_id),
        local_date: date,
        check_in: checkInTime, // Déjà en ISO string depuis createTimestamp
        check_out: checkOutTime, // Déjà en ISO string depuis createTimestamp
        break_minutes: breakMinutes,
        work_minutes: workMinutes
      };
      
      const url = API_ENDPOINTS.ATTENDANCE_DAY_CREATE();
      const res = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Erreur lors de la création');
      }
      
      const result = await res.json();
      const newDay = result.day;
      
      // Trouver le nom de l'opérateur
      const operator = operators.find(op => op.id === Number(formData.operator_id));
      
      // Formatter la nouvelle ligne pour le tableau
      const newRow = {
        id: newDay.id,
        local_date: date,
        operator_id: Number(formData.operator_id),
        operator_name: operator?.name || 'Inconnu',
        check_in: checkInTime, // Déjà en ISO string
        check_out: checkOutTime, // Déjà en ISO string
        break_minutes: breakMinutes,
        work_minutes: workMinutes
      };
      
      // 🔥 Ajouter au cache INSTANTANÉMENT (pas de refresh API)
      addAttendanceToCache(newRow);
      
      showToast('Présence ajoutée avec succès', 'success');
      
      // Le cache emit un event → l'UI se met à jour automatiquement
      // Retourner la nouvelle ligne pour que le tableau l'ajoute localement
      return newRow;
      
    } catch (err) {
      console.error('Erreur création:', err);
      showToast(err.message || 'Erreur lors de la création', 'error');
      return null;
    }
  };



  // Affichage des données
  const hasData = !loading && !error && weeks.length > 0;
  const isEmpty = !loading && !error && weeks.length === 0;

  return (
    <div className="activity-page">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="logo">CabConnect</div>
        <nav className="nav-menu">
          <div className="nav-item" onClick={() => onNavigate && onNavigate('tasks')}>
            <span className="nav-icon">📁</span>
            <span>Tâches</span>
          </div>
          <div
            className="nav-item"
            role="button"
            tabIndex={0}
            onClick={() => onNavigate && onNavigate('sterilization')}
            onKeyDown={(e) => { 
              if (e.key === 'Enter' || e.key === ' ') onNavigate && onNavigate('sterilization'); 
            }}
          >
            <span className="nav-icon">📦</span>
            <span>Stérilisation</span>
          </div>
          <div
            className="nav-item"
            role="button"
            tabIndex={0}
            onClick={() => onNavigate && onNavigate('operators')}
            onKeyDown={(e) => { 
              if (e.key === 'Enter' || e.key === ' ') onNavigate && onNavigate('operators'); 
            }}
          >
            <span className="nav-icon">👤</span>
            <span>Opérateurs</span>
          </div>
          <div
            className="nav-item active"
            role="button"
            tabIndex={0}
            onClick={() => onNavigate && onNavigate('activity')}
            onKeyDown={(e) => { 
              if (e.key === 'Enter' || e.key === ' ') onNavigate && onNavigate('activity'); 
            }}
          >
            <span className="nav-icon">📊</span>
            <span>Activité</span>
          </div>
          <div
            className="nav-item"
            role="button"
            tabIndex={0}
            onClick={() => onNavigate && onNavigate('agenda')}
            onKeyDown={(e) => { 
              if (e.key === 'Enter' || e.key === ' ') onNavigate && onNavigate('agenda'); 
            }}
          >
            <span className="nav-icon">🗓️</span>
            <span>Agenda</span>
          </div>
          <div
            className="nav-item"
            role="button"
            tabIndex={0}
            onClick={() => onNavigate && onNavigate('orders')}
            onKeyDown={(e) => { 
              if (e.key === 'Enter' || e.key === ' ') onNavigate && onNavigate('orders'); 
            }}
          >
            <span className="nav-icon">🛒</span>
            <span>Commandes</span>
          </div>
        </nav>
      </div>

      {/* Contenu principal */}
      <div className="activity-content">
        {/* En-tête */}
        <div className="activity-header">
          <h1>Activité — Heures par semaine</h1>
          <div className="activity-actions">
            {isRefreshing && (
              <span className="refreshing-indicator" style={{ marginRight: '10px', color: '#5fa8ff' }}>
                ⟳ Mise à jour...
              </span>
            )}
            <button className="refresh-btn" onClick={refresh} disabled={isRefreshing || loading}>
              ↻ Rafraîchir
            </button>
          </div>
        </div>

        {/* Carte du graphique */}
        <div className="card chart-card">
          <div className="card-header">
            <h3>Nombre d'heures réalisées par semaine et par opérateur</h3>
            <div className="chart-month-navigation">
              <button 
                className="arrow-btn" 
                onClick={() => handleMonthChange('prev')}
                title="Mois précédent"
              >
                ←
              </button>
              <span className="current-month">
                {formatMonthDisplay(selectedMonth)}
              </span>
              <button 
                className="arrow-btn" 
                onClick={() => handleMonthChange('next')}
                title="Mois suivant"
              >
                →
              </button>
            </div>
          </div>
          <div className="card-body">
            {/* État: Erreur (seul cas bloquant) */}
            {error && !loading && (
              <div className="error">
                <div>Erreur lors du chargement des données.</div>
                <code>{String(error.message || error)}</code>
                <button onClick={refresh} className="retry-btn">
                  Réessayer
                </button>
              </div>
            )}

            {/* État: Vide (mais pas en chargement) */}
            {isEmpty && !loading && !isRefreshing && (
              <div className="chip muted">
                Aucune donnée sur la période sélectionnée
              </div>
            )}

            {/* Graphique TOUJOURS affiché (avec effet shimmer pendant chargement) */}
            {(hasData || loading || isRefreshing) && (
              <div className={`chart-container ${loading ? 'chart-loading' : ''} ${isRefreshing ? 'chart-refreshing' : ''}`}>
                <WeeklyHoursChart
                  key={`chart-${range.from}-${range.to}-${JSON.stringify(series)}`}
                  weeks={weeks.length > 0 ? weeks : ['--', '--', '--', '--']}
                  operators={operators}
                  series={series}
                  maxY={maxY || 50}
                />
              </div>
            )}
          </div>
        </div>

        {/* Tableaux mensuels - Opérateurs et Salaires */}
        <div className="monthly-summary-section">
          <div className="monthly-summary-container">
            <OperatorsSummary
              operators={operators}
              series={series}
              currentMonth={selectedMonth}
              rawData={rawData}
            />
            <SalarySummary
              operators={operators}
              series={series}
              rawData={rawData}
              currentMonth={selectedMonth}
              onMonthChange={handleMonthChange}
              onNavigate={onNavigate}
            />
          </div>
        </div>

        {/* Tableau des présences */}
        <AttendanceTable 
          data={rawData || []} 
          onUpdate={handleUpdateAttendance}
          onAdd={handleAddAttendance}
          allOperators={operators}
        />

        {/* Toast de notification */}
        {toast && (
          <div className={`toast toast-${toast.type}`}>
            <span className="toast-icon">
              {toast.type === 'success' && '✓'}
              {toast.type === 'error' && '✕'}
              {toast.type === 'info' && 'ℹ'}
            </span>
            <span className="toast-message">{toast.message}</span>
          </div>
        )}
      </div>
    </div>
  );
}